// app/src/lib/delivery/upload.js — upload a delivery-statement PDF end-to-end.
//
// Flow: presign+PUT (kind=delivery) → poll the delivery OCR result → persist a
// canonical statement. The OCR may not resolve platform/period; the caller can
// pass hints (what the user selected at upload) and later correct via
// updateStatement(). Returns the saved row plus a `needsReview` signal.

import { uploadReceiptImage, pollOcrResult } from '../ocr/uploader.js';
import { saveStatement } from './statements.js';
import { normalizeStatement } from './normalizer.js';

/**
 * @param {Blob} pdfBlob - the statement PDF (application/pdf)
 * @param {Object} [hint] - { platform, periodMonth } user selections
 * @param {Object} [opts] - { timeoutMs }
 * @returns {Promise<Object>} the saved statement row
 */
export async function uploadDeliveryStatement(pdfBlob, hint = {}, opts = {}) {
  if (!(pdfBlob instanceof Blob)) throw new Error('delivery: not a Blob');
  if (pdfBlob.type && pdfBlob.type !== 'application/pdf') throw new Error('delivery: statement must be a PDF');

  // The cap is per reporting month and the month isn't known until OCR reads it,
  // so saveStatement enforces it after parsing (see statements.js).
  const { receiptId, objectKey } = await uploadReceiptImage(pdfBlob, 'application/pdf', { kind: 'delivery' });
  const extract = await pollOcrResult(receiptId, { kind: 'delivery', timeoutMs: opts.timeoutMs || 90_000 });

  // Guard against the wrong DoorDash export: a "Subtotal Tax Breakdown Report"
  // (or any non-payout document) has no Net total / payout to reconcile against.
  // Reject it with a clear message instead of saving a confusing statement.
  const preview = normalizeStatement(extract, { platform: hint.platform, periodMonth: hint.periodMonth });
  if (!(preview.netPayoutCents > 0)) {
    const err = new Error(
      'No payout found on this document — it looks like a tax/subtotal report, not a payout statement. '
      + 'Upload the platform "Statement" (the one with a Net total / "Total payments to you").',
    );
    err.code = 'DELIVERY_NO_PAYOUT';
    throw err;
  }
  return saveStatement(extract, { ...hint, receiptObjectKey: objectKey });
}
